// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import usePopup from "@/hooks/usePopup";
import type { TySelection } from "@/hooks/useSelection";
import BulkResultPopup from "@/UI/popups/BulkResultPopup";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
// --- Logic --------------------------------------------------------------------------------------
type TyAnswer = { updated?: number; deleted?: number; restored?: number; purged?: number; units?: number; skipped?: number };

type TyOptions = {
    /** The resource's own path. `/bulk` is appended, or passed per call for the trash's two routes. */
    path: string;
    selection: TySelection;
    /** Reload the list. Called on success, after the selection is dropped. */
    onDone: () => void;
    /**
     * What identifies the selection to the API. The default is `{ ids }`; the trash overrides it,
     * because its rows come from nine collections and an id alone does not say which.
     */
    body?: () => Record<string, unknown>;
};

type TyConfirmed = {
    title: string;
    message: string;
    confirmLabel: string;
    /** Adds the typed confirmation, for the one delete with no way back. */
    word?: string;
    path?: string;
    method?: "POST" | "PATCH" | "DELETE";
    /** `false` for anything that is not destructive — the trash's restore is the one so far. */
    danger?: boolean;
    /**
     * How the result popup counts what landed: deleted, restored, purged. It rides on the call rather
     * than on the hook because one screen both edits and deletes, and "12 changed" is the wrong
     * sentence for a delete.
     */
    doneLabel?: string;
};

export type TyBulk = {
    busy: boolean;
    /** A refusal, kept **in the bar** — next to the button that was pressed, with the selection kept. */
    note: string | null;
    apply: (set: Record<string, unknown>) => void;
    /** Anything destructive. The confirmation lives here so that no caller can forget it. */
    confirmed: (options: TyConfirmed) => void;
};

/**
 * The write half of the bulk bar: one call, the selection cleared when it lands, and **a popup saying
 * what the write actually did**.
 *
 * That last part is why this exists rather than a bare `useFetch`. A bulk write is the one call in
 * this app whose answer is two numbers — it can land on thirty-seven of forty, and the three it did
 * not reach are the three somebody has to go and look at. A count printed in a line under the list is
 * where a person who has just pressed a button is not looking, so it is said in front of them.
 *
 * A refusal is reported **twice on purpose**: in the popup, because the action finished and finishing
 * is what the popup is for, and in the bar, because the selection is still there and so is the retry.
 */
export default function useBulk({ path, selection, onDone, body }: TyOptions): TyBulk {
    const addComp = usePopup((state) => state.addComp);
    const call = useFetch<TyAnswer>(path, {}, false);
    /**
     * How many were asked for and what to call them, kept because the selection is dropped and the
     * call's own options are gone by the time the answer comes back.
     */
    const [asked, setAsked] = useState(0);
    const [said, setSaid] = useState<string | undefined>(undefined);

    const report = (details: { done: number; failed: number; error?: string | null }) =>
        addComp({ name: "bulk-result", Component: BulkResultPopup, anim: "scale", details: { ...details, doneLabel: said } });

    useEffect(() => {
        if (!call.succeededAt) return;
        const answer = call.data;
        const done = answer?.updated ?? answer?.deleted ?? answer?.restored ?? answer?.purged ?? 0;

        selection.clear();
        onDone();
        report({ done, failed: answer?.skipped ?? Math.max(0, asked - done) });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [call.succeededAt]);

    /** A refusal is a finished action too, and nothing in it landed — so it says both. */
    useEffect(() => {
        if (!call.error) return;
        report({ done: 0, failed: asked, error: call.error });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [call.error]);

    const read = () => body?.() ?? { ids: selection.ids };

    /** Every call goes through here, so the figure the popup reports can never drift from the call. */
    const send = (options: { method: "POST" | "PATCH" | "DELETE"; path: string; data: Record<string, unknown> }, label?: string) => {
        setAsked(selection.count);
        setSaid(label);
        call.invoke(options);
    };

    return {
        busy: call.loading,
        note: call.error,
        apply: (set) => send({ method: "PATCH", path: `${path}/bulk`, data: { ...read(), set } }),
        confirmed: ({ title, message, confirmLabel, word, path: to, method, danger, doneLabel }) =>
            addComp({
                name: "confirm",
                Component: ConfirmPopup,
                anim: "scale",
                details: {
                    title,
                    message,
                    confirmLabel,
                    confirmWord: word,
                    danger,
                    onConfirm: () => send({ method: method ?? "DELETE", path: to ?? `${path}/bulk`, data: read() }, doneLabel),
                },
            }),
    };
}
